/**
 * HIRO — Auto free-gift for Quarterly subscriptions
 * ---------------------------------------------------
 * Adds a £0 "HIRO Bottle" gift line whenever the cart contains a QUARTERLY
 * subscription line, and removes it when no quarterly line remains.
 *
 * The gift is added ONE-TIME (no selling plan), so Skio renewal orders never
 * include it — that gives the "free bottle on the first box only" rule for free.
 *
 * TRIGGER: we intercept the cart network requests themselves (add / change /
 * update / clear). Earlier we hooked the theme's cart:* events, but drawer
 * REMOVALS re-render without firing an event we could hear, so the gift lingered
 * until a full page reload. Intercepting fetch + XHR catches every mutation.
 *
 * Loop-safe: reconcile() does its OWN cart reads/writes through the NATIVE fetch
 * captured before patching, so its calls are never re-intercepted. It only
 * dispatches cart:refresh (which re-renders the drawer) when it actually changed
 * the cart, so everything settles.
 *
 * Safe before the gift product exists: resolveGiftVariant() returns null and the
 * script no-ops until the product (handle GIFT_HANDLE) is created.
 */
(function () {
  'use strict';

  // ---- CONFIG (edit here) --------------------------------------------------
  var GIFT_HANDLE = 'hiro-bottle-free-gift';         // product handle of the £0 gift
  var QUARTERLY_PLAN_IDS = [1421902001];             // "Every 3 Months" on hydration
  var QUARTERLY_NAME_RE = /(3\s*month|quarter|12\s*week|13\s*week|90\s*day)/i;
  // Free bottle rides quarterly ONLY on hydration + creatine + superblends (incl. their
  // twin/triple pack products). Signal / Daily Stack are excluded. Keep in sync with subscribe-box.liquid.
  var GIFT_ELIGIBLE_RE = /(hydration|creatine|essential|signal)/i;
  var LOG = '[HIRO Gift]';
  // -------------------------------------------------------------------------

  // Native fetch captured BEFORE we patch window.fetch — reconcile uses this so
  // its own cart reads/writes are never re-intercepted.
  var nativeFetch = window.fetch ? window.fetch.bind(window) : null;
  var CART_MUTATION_RE = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/i;

  var giftVariantId = null;   // resolved lazily from the product handle
  var busy = false;
  var timer = null;

  function isQuarterly(item) {
    var a = item && item.selling_plan_allocation;
    if (!a || !a.selling_plan) return false;
    if (QUARTERLY_PLAN_IDS.indexOf(a.selling_plan.id) > -1) return true;
    return QUARTERLY_NAME_RE.test(a.selling_plan.name || '');
  }

  function isGift(item) {
    return !!item && item.handle === GIFT_HANDLE;
  }

  function post(url, body) {
    return nativeFetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(body)
    });
  }

  function resolveGiftVariant() {
    if (giftVariantId) return Promise.resolve(giftVariantId);
    if (!nativeFetch) return Promise.resolve(null);
    return nativeFetch('/products/' + GIFT_HANDLE + '.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (p) {
        if (p && p.variants && p.variants.length) { giftVariantId = p.variants[0].id; }
        return giftVariantId;
      })
      .catch(function () { return null; });
  }

  // Re-render the cart drawer the way the theme does (binds to _rerenderCart).
  function refreshCart() {
    document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
  }

  function reconcile() {
    if (busy || !nativeFetch) return Promise.resolve();
    busy = true;
    return nativeFetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var items = cart.items || [];
        var hasQuarterly = items.some(function (it) { return isQuarterly(it) && GIFT_ELIGIBLE_RE.test(it.handle || ''); });
        var giftLine = null;
        for (var i = 0; i < items.length; i++) { if (isGift(items[i])) { giftLine = items[i]; break; } }

        // 1) Should have a gift but doesn't → add it (one-time, qty 1, no plan)
        if (hasQuarterly && !giftLine) {
          return resolveGiftVariant().then(function (vid) {
            if (!vid) { console.warn(LOG, 'gift product not found (handle ' + GIFT_HANDLE + '); skipping'); return; }
            return post('/cart/add.js', { id: vid, quantity: 1, properties: { _free_gift: 'quarterly' } })
              .then(function (res) { if (res.ok) refreshCart(); else console.warn(LOG, 'add failed', res.status); });
          });
        }

        // 2) Has a gift line that shouldn't be there → remove it
        if (giftLine && !hasQuarterly) {
          return post('/cart/change.js', { id: giftLine.key, quantity: 0 })
            .then(function (res) { if (res.ok) refreshCart(); });
        }

        // 3) Gift present but malformed (qty != 1, or it picked up a selling plan) → strip it;
        //    the next pass re-adds a clean one because a quarterly line is still present.
        if (giftLine && hasQuarterly && (giftLine.quantity !== 1 || giftLine.selling_plan_allocation)) {
          return post('/cart/change.js', { id: giftLine.key, quantity: 0 })
            .then(function (res) { if (res.ok) refreshCart(); });
        }
        // else: consistent — do nothing (loop settles here)
      })
      .catch(function (e) { console.error(LOG, e); })
      .then(function () { busy = false; });
  }

  // Debounce bursts of triggers into a single reconcile. The delay lets the
  // theme's own drawer render settle first, then we add/remove the gift.
  function schedule() {
    if (timer) clearTimeout(timer);
    timer = setTimeout(reconcile, 250);
  }

  // ---- Trigger 1: intercept cart-mutating network requests -----------------
  if (nativeFetch) {
    window.fetch = function (input, init) {
      var result = nativeFetch(input, init);
      try {
        var url = (typeof input === 'string' ? input : (input && input.url)) || '';
        if (CART_MUTATION_RE.test(url)) { result.then(function () { schedule(); }).catch(function () {}); }
      } catch (e) {}
      return result;
    };
  }

  // XHR fallback (in case any cart op goes through XMLHttpRequest)
  if (window.XMLHttpRequest) {
    var origOpen = XMLHttpRequest.prototype.open;
    var origSend = XMLHttpRequest.prototype.send;
    XMLHttpRequest.prototype.open = function (method, url) {
      this.__hiroCartMutation = CART_MUTATION_RE.test(url || '');
      return origOpen.apply(this, arguments);
    };
    XMLHttpRequest.prototype.send = function () {
      if (this.__hiroCartMutation) {
        this.addEventListener('loadend', function () { schedule(); });
      }
      return origSend.apply(this, arguments);
    };
  }

  // ---- Trigger 2: theme cart lifecycle events (belt & suspenders) ----------
  document.addEventListener('product:added', schedule);
  document.addEventListener('cart:refreshed', schedule);
  document.addEventListener('cart:opened', schedule);

  // ---- Trigger 3: initial page load (covers landing with a quarterly in cart)
  document.addEventListener('DOMContentLoaded', function () {
    resolveGiftVariant();  // warm the variant id
    schedule();
  });

  console.log(LOG, 'auto free-gift script loaded (network-intercept)');
})();
